import { isValid, parseJSON } from 'date-fns';
// TODO: Representing geographical _points_ and _areas_ with the same scalar
// type is probably not ideal.
//
// NB: the ID type refers to ids for any type of entity, but USER_ID should be
// used instead for fields that hold ids of users protego might know about. E.g.,
// imagine a `Message` content type. Both the `to` and `from` fields could hold
// ScalarTypes.USER_IDs, and then a rule could flag the message if _either_ the
// sender or the recipient satisfies some condition (after passing the user id
// to a user-related signal).
export const ScalarTypes = makeEnumLike([
    'USER_ID',
    'ID',
    'STRING',
    'BOOLEAN',
    'NUMBER',
    'AUDIO',
    'IMAGE',
    'VIDEO',
    'DATETIME',
    'GEOHASH',
    'RELATED_ITEM',
    'URL',
    'POLICY_ID',
]);
export const ContainerTypes = makeEnumLike(['ARRAY', 'MAP']);
const containerTypes = new Set(Object.values(ContainerTypes));
export function isContainerField(it) {
    return isContainerType(it.type);
}
export function isContainerType(it) {
    return containerTypes.has(it);
}
export function getScalarType(it) {
    return (isContainerField(it) ? it.container.valueScalarType : it.type);
}
export function isMediaType(it) {
    return (it === ScalarTypes.AUDIO ||
        it === ScalarTypes.VIDEO ||
        it === ScalarTypes.IMAGE);
}
export function isMediaValue(it) {
    return isMediaType(it.type);
}
/**
 * Takes an array of strings and returns an object with a property for each
 * string in the array, where the string is used as both the name and value for
 * the property.
 *
 * This is useful to get type safety and automatic refactoring in some cases.
 * E.g., imagine you're setting the default value for a field on a Sequelize
 * model. Let's say the field can have three legal values: 'a', 'b', or 'c'.
 * So, you'll initialize the model with some config object for the field, like
 * `{ defaultValue: 'a' }`. Now, the type that this `defaultValue` key expects
 * will be very vague -- likely `string` or maybe even `any` -- because it was
 * defined by Sequelize and doesn't know about your field's specific legal
 * values. Therefore, you could write `{ defaultValue: 'invalid' }` and TS
 * wouldn't complain; moreover, even if you wrote `{ defaultValue: 'a' }`, which
 * would be correct at the time, a rename on the value 'a' would not
 * automatically rename the value here, because they're not linked by type.
 *
 * To fix these issues, it can be very helpful to have an object like
 * `const legalValues = { 'a': 'a', 'b': 'b', 'c': 'c' };` because, then,
 * you can do `{ defaultValues: legalValues.a }`, and you're guaranteed a typo-
 * free and rename-friendly value. That `legalValues` object is what this
 * function makes.
 *
 * Obviously, such an object is similar to a TS enum (hence this function's
 * name). The key difference, though, is that the values in this object are
 * typed as string literals, whereas the value for each case in an enum is
 * treated by the type system as intentionally opaque. So, having that
 * visibility in an 'enum-like' can help a lot with assignability, when the
 * source value is a string literal type (rather than the source having to have
 * been constructed with the same enum).
 *
 * We also exploit this for assigning values that come in from GraphQL. The
 * GraphQL value is an enum; we want to use a different type in our inner layers
 * (which shouldn't be coupled to GraphQL); but, if our internal type were an
 * enum, the GraphQL enum wouldn't be assignable to it (even if their runtime
 * values match). However, if the internal type is an "enum like", then TS will
 * allow GraphQL enum to be assignable to it iff the enum's runtime values are
 * legal values in the enum like.
 */
export function makeEnumLike(strings) {
    return Object.fromEntries(strings.map((it) => [it, it]));
}
export const hiveProjectNames = [
    'Visual',
    'Text',
    'Speech',
    'OCR',
    'Demographic',
];
export function isHiveProjectName(name) {
    return hiveProjectNames.includes(name);
}
export function parseDateString(it) {
    return new Date(it);
}
/**
 * Returns a DateString if the input string can be parsed
 * as a date; else undefined. Accepts strings in a rather limited set
 * of formats for now. (See {@link parseJSON} docs for details.)
 */
export function makeDateString(it) {
    const potentialDate = parseJSON(it);
    // check if the parsing succeeded; return accordingly
    return isValid(potentialDate)
        ? potentialDate.toISOString()
        : undefined;
}
export const ItemTypeKind = makeEnumLike(['CONTENT', 'THREAD', 'USER']);
